import { Card, CardBody, CardExtra, CardHead, CardTitle } from "@/components/ui/Card.jsx";
import Tag from "@/components/ui/Tag.jsx";
import { EmptyState } from "@/components/ui/Display.jsx";
import { formatDuration, formatDateTime, compareVersion } from "@/lib/format.js";
import { PRODUCTIVITY, avatarTone, toneOf } from "@/lib/tokens.js";
import { cx } from "@/lib/cx.js";

/* ---- Time distribution -------------------------------------------------- */

/*
 * The stacked bar and its legend.
 *
 * Segments are laid out from raw milliseconds rather than from the API's
 * pre-rounded `statusDistribution` percentages: four independently rounded
 * whole numbers rarely total 100, so the bar would end short or overflow. The
 * legend still shows one decimal, which is where the rounding belongs.
 */
export function TimeDistribution({ distribution }) {
    const { rows, total } = distribution;

    return (
        <Card>
            <CardHead>
                <CardTitle>Time Distribution</CardTitle>
                <CardExtra>
                    <span className="text-xs text-slate-400 dark:text-slate-500">
                        of {formatDuration(total)} rated
                    </span>
                </CardExtra>
            </CardHead>
            <CardBody>
                {total === 0 ? (
                    <p className="py-6 text-center text-[13px] text-slate-400 dark:text-slate-500">
                        No rated activity in this range.
                    </p>
                ) : (
                    <>
                        <div
                            data-dist-bar
                            className="mb-4 flex h-2.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800"
                        >
                            {rows.map((r) => (
                                <span
                                    key={r.key}
                                    data-dist-segment={r.key}
                                    title={`${r.label} ${formatDuration(r.ms)}`}
                                    style={{ width: `${r.percent}%` }}
                                    className={cx(
                                        "h-full first:rounded-l-full last:rounded-r-full",
                                        toneOf(r.tone).bar,
                                    )}
                                />
                            ))}
                        </div>

                        <div className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
                            {rows.map((r) => (
                                <div key={r.key} data-dist-row={r.key} className="flex items-center gap-2.5 py-1">
                                    <span
                                        className={cx("size-2.5 shrink-0 rounded-sm", toneOf(r.tone).bar)}
                                    />
                                    <span className="text-[13px] font-medium">{r.label}</span>
                                    <span
                                        className={cx(
                                            "ml-auto font-mono text-[12.5px] font-semibold",
                                            toneOf(r.tone).text,
                                        )}
                                    >
                                        {formatDuration(r.ms)}
                                    </span>
                                    <span className="w-11 text-right font-mono text-[11.5px] text-slate-400 dark:text-slate-500">
                                        {r.percent.toFixed(1)}%
                                    </span>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </CardBody>
        </Card>
    );
}

/* ---- Activity timeline -------------------------------------------------- */

export function ActivityTimeline({ points }) {
    return (
        <Card>
            <CardHead>
                <CardTitle>Activity Timeline</CardTitle>
            </CardHead>
            <CardBody>
                <ol className="relative space-y-4 border-l border-slate-200 pl-5 dark:border-slate-800">
                    {points.map((p) => (
                        <li key={p.key} data-timeline={p.key} className="relative">
                            <span
                                className={cx(
                                    "absolute top-1.5 -left-[26px] size-2.5 rounded-full ring-3 ring-white dark:ring-slate-900",
                                    toneOf(p.tone).bar,
                                )}
                            />
                            <p className="text-[11px] font-semibold tracking-wider text-slate-400 uppercase dark:text-slate-500">
                                {p.label}
                            </p>
                            <p className="mt-0.5 font-mono text-[15px] font-semibold">
                                {p.at ? formatDateTime(p.at) : "—"}
                            </p>
                            <p className="text-[11.5px] text-slate-400 dark:text-slate-500">{p.note}</p>
                        </li>
                    ))}
                </ol>
            </CardBody>
        </Card>
    );
}

/* ---- Usage lists -------------------------------------------------------- */

/*
 * Applications / Browsers / Websites. The bar scale is shared across all three
 * cards — see usageLists() for why.
 */
export function UsageList({ title, list, onOpen, emptyHint }) {
    return (
        <Card>
            <CardHead>
                <CardTitle>{title}</CardTitle>
                <CardExtra>
                    <Tag tone="slate" data-usage-count={title}>
                        {list.rows.length}
                    </Tag>
                </CardExtra>
            </CardHead>

            {list.rows.length === 0 ? (
                <CardBody>
                    <p className="py-6 text-center text-[13px] text-slate-400 dark:text-slate-500">
                        {emptyHint}
                    </p>
                </CardBody>
            ) : (
                <div className="flex flex-col">
                    {list.rows.map((row) => {
                        const p = PRODUCTIVITY[row.productivityStatus] ?? PRODUCTIVITY.unrated;
                        const tone = toneOf(p.tone);

                        return (
                            <button
                                key={row.key}
                                type="button"
                                data-usage={row.name}
                                onClick={() => onOpen(row)}
                                className="flex w-full min-w-0 flex-col gap-1.5 border-b border-slate-100 px-4.5 py-3 text-left transition last:border-0 hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800/60"
                            >
                                <span className="flex min-w-0 items-center gap-2">
                                    {/* Colour derived from the name, so an app
                                        is the same swatch on every card and
                                        across reloads. */}
                                    <span
                                        className={cx(
                                            "grid size-5 shrink-0 place-items-center rounded text-[9px] font-semibold text-white",
                                            avatarTone(row.name),
                                        )}
                                    >
                                        {row.name.charAt(0).toUpperCase()}
                                    </span>
                                    <span className="truncate text-[13px] font-medium text-brand-600 dark:text-brand-400">
                                        {row.name}
                                    </span>
                                    <span className={cx("tag shrink-0", tone.tag)}>{p.label}</span>
                                    <span className="ml-auto shrink-0 font-mono text-[12.5px] font-semibold">
                                        {formatDuration(row.ms)}
                                    </span>
                                </span>
                                <span className="flex h-1.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                                    <span
                                        className={cx("h-full rounded-full", tone.bar)}
                                        style={{ width: `${row.percent}%` }}
                                    />
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}
        </Card>
    );
}

/* ---- System information ------------------------------------------------- */

export function SystemInfoCard({ systemInfo, latestVersion }) {
    if (!systemInfo) {
        return (
            <Card>
                <CardHead>
                    <CardTitle>System Information</CardTitle>
                </CardHead>
                <CardBody>
                    <EmptyState
                        title="No device has reported in"
                        hint="System information appears once the desktop app has been installed and signed in on this account."
                    />
                </CardBody>
            </Card>
        );
    }

    // Semver-aware, not a string ===: a device on 2.1.6-dev must NOT be told to
    // "update" to an older 2.1.0. Up to date = at or ahead of the latest; the
    // -dev suffix is ignored by parseVersion. No version reported → don't nag.
    const current = !systemInfo.appVersion || compareVersion(systemInfo.appVersion, latestVersion) >= 0;

    return (
        <Card>
            <CardHead>
                <CardTitle>System Information</CardTitle>
                <CardExtra>
                    <Tag tone={current ? "emerald" : "amber"} data-version-state>
                        {current ? "Up to date" : `Update available — ${latestVersion}`}
                    </Tag>
                </CardExtra>
            </CardHead>
            <CardBody>
                {/* Everything the desktop reports on registration + each sync, so
                    the whole device fingerprint is visible in one place. */}
                <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <Fact label="Device Name" value={systemInfo.deviceName} />
                    <Fact label="Operating System" value={systemInfo.os} />
                    <Fact label="OS Version" value={systemInfo.osVersion} mono />
                    <Fact label="App Version" value={systemInfo.appVersion} mono />
                    <Fact label="Serial Number" value={systemInfo.serialNumber} mono />
                    <Fact label="MAC Address" value={systemInfo.macAddress} mono />
                    <Fact label="IP Address" value={systemInfo.ipAddress} mono />
                    <Fact label="Timezone" value={systemInfo.timezone} />
                    <Fact
                        label="Last reported"
                        value={systemInfo.updatedAt ? formatDateTime(systemInfo.updatedAt) : null}
                    />
                </dl>
            </CardBody>
        </Card>
    );
}

function Fact({ label, value, mono }) {
    return (
        <div>
            <dt className="dt">{label}</dt>
            <dd className={cx("dd", mono && "font-mono")}>{value || "—"}</dd>
        </div>
    );
}
